import React, { memo } from "react";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { AccrualMatchPreview } from "@components/accounting/accrualReview/AccrualMatchPreview";
import { aggregateMatches, MatchSummaryItem } from "./aggregateMatches";
import styles from "./AccrualMatchPreviewContent.module.scss";

interface Props {
    readonly accrualMatches: readonly MatchSummaryItem[];
    readonly onClick: () => void;
    readonly readonly?: boolean;
    readonly isSuggested?: boolean;
}

/**
 * Cell-friendly preview of a transaction's accrual matches, reused by the
 * Accrual Review match column and the consolidated "Match" column on
 * regular transactions tables. `isSuggested` applies the tasks suggested-match
 * treatment: blue text and a blue count pill.
 *
 * Renders an empty editable cell when there are no matches; otherwise
 * shows an aggregated preview (first counterparty plus a `+N` chip when
 * multiple distinct counterparties, summed amount, and a count chip).
 * Click dispatches to the supplied opener (typically opens the accrual
 * match drawer).
 */
export const AccrualMatchPreviewContent: React.FC<Props> = memo(
    ({ accrualMatches, onClick, readonly, isSuggested }) => {
        const aggregated = aggregateMatches(accrualMatches);

        // Empty cell when there's nothing renderable. Covers both an empty
        // input and the edge case where every match row is unresolvable
        // (no invoice, bill, or pairedTransaction) — otherwise we'd show
        // "$0.00" / "0 matches" in a populated pill.
        if (aggregated.resolvedCount === 0) {
            return (
                <ViewTableCell captureClicks className={styles.cell}>
                    <EditableCellContent
                        isEmpty
                        onClick={onClick}
                        readonly={readonly}
                    />
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks className={styles.cell}>
                <EditableCellContent
                    className={styles.matchContent}
                    isEmpty={false}
                    onClick={onClick}
                    readonly={readonly}
                >
                    <AccrualMatchPreview
                        counterpartyName={aggregated.firstName}
                        extraCounterpartyCount={aggregated.extraNamesCount}
                        amount={aggregated.totalAmount}
                        countLabel={aggregated.countLabel}
                        isSuggested={isSuggested}
                    />
                </EditableCellContent>
            </ViewTableCell>
        );
    },
);

AccrualMatchPreviewContent.displayName = "AccrualMatchPreviewContent";
